import React from 'react';

import merge from 'lodash/merge';

import { useResource } from '@atlassian/eoc-rrr-helpers';
import { SingleViewShiftCard } from '@atlassian/eoc-schedule-common';

import { ShiftAction, ShiftStatus } from '../../../../common/types';
import { MultiShiftCard } from '../../../../common/ui/shift-card/card-multi';
import { MultiQuickOverrideShiftCard } from '../../../../common/ui/shift-card/card-multi-quick-override';
import { SingleShiftCard } from '../../../../common/ui/shift-card/card-single';
import { SingleOverridenShiftCard } from '../../../../common/ui/shift-card/card-single-overriden';
import { SingleQuickOverrideShiftCard } from '../../../../common/ui/shift-card/card-single-quick-override';
import { useActions as usePageActions } from '../../../../controllers/page-store';
import { useActions as useScheduleActions } from '../../../../controllers/schedule-store';
import { useSchedule, useTimelineEpoch } from '../../../../controllers/schedule-store/helpers';
import { useStore as useShiftStore } from '../../../../controllers/shift-store';
import { getScheduleRightsResource } from '../../../../services/resources';

import { type ActiveCardProps } from './types';
import { makeTime, normalizeRange } from './utils';

const ActiveCard = (props: ActiveCardProps) => {
	const { onClose } = props;

	const { data: scheduleRights } = useResource(getScheduleRightsResource);

	const [{ cards, status }, { act }] = useShiftStore();

	const [outer] = useTimelineEpoch();
	const [schedule] = useSchedule();

	const { openCustomizeOverride, openEditOverride, openRevertOverride } = usePageActions();
	const { addOverrideAction, addOverrideBulkAction, getOverrideAction } = useScheduleActions();

	const single = cards[0]!;

	if (status === ShiftStatus.SingleViewOnly) {
		return <SingleViewShiftCard cards={cards} onClose={onClose} />;
	}

	if (status === ShiftStatus.Single) {
		return (
			<SingleShiftCard
				cards={cards}
				onClose={onClose}
				onQuickOverride={() => act(ShiftAction.AddOverride)}
			/>
		);
	}

	if (status === ShiftStatus.SingleQuickOverride) {
		return (
			<SingleQuickOverrideShiftCard
				cards={[{ ...single, ...normalizeRange(single, outer) }]}
				onClose={onClose}
				onCancel={() => act(ShiftAction.Cancel)}
				isDisabled={!scheduleRights!.hasEditRight}
				onAddOverride={async (autoCompleteItem) => {
					const responder = {
						id: autoCompleteItem.atlassianIdentifier!,
						type: autoCompleteItem.type,
					};

					const range = normalizeRange(single, outer);

					await addOverrideAction({
						startTime: makeTime(range.startEpoch, range.startOffset),
						endTime: makeTime(range.endEpoch, range.endOffset),
						timezone: schedule.timezone,
						rotationIds: [single.rotation.id],
						responder,
					});

					onClose();
				}}
				onCustomize={(autoCompleteItem) => {
					const responder = autoCompleteItem
						? {
								id: autoCompleteItem.value,
								type: autoCompleteItem.type,
							}
						: undefined;

					const range = normalizeRange(single, outer);

					const override = merge({}, range, {
						rotations: [single.rotation],
						responder,
					});

					const context = {
						scheduleId: schedule.id,
					};

					openCustomizeOverride({
						override,
						context,
						id: ['customize-override-button', schedule.id],
					});
				}}
			/>
		);
	}

	if (status === ShiftStatus.SingleOverriden) {
		return (
			<SingleOverridenShiftCard
				cards={cards}
				onClose={onClose}
				onRevert={async () => {
					const object = await getOverrideAction({ alias: single.alias! });

					const {
						startTimeUtcMillis: startEpoch,
						endTimeUtcMillis: endEpoch,
						startOffsetMillis: startOffset,
						endOffsetMillis: endOffset,
						rotationIds,
						responder,
					} = object!;

					const range = normalizeRange(
						{
							startEpoch,
							endEpoch,
							startOffset,
							endOffset,
						},
						outer,
					);

					const override = merge({}, range, {
						alias: single.alias!,
						rotations: rotationIds.map((value) => ({ id: value })),
						responder,
					});

					const context = {
						scheduleId: schedule.id,
					};

					openRevertOverride({
						override,
						context,
						id: ['revert-override-button', schedule.id],
					});
				}}
				onEdit={async () => {
					const object = await getOverrideAction({ alias: single.alias! });

					const {
						startTimeUtcMillis: startEpoch,
						endTimeUtcMillis: endEpoch,
						startOffsetMillis: startOffset,
						endOffsetMillis: endOffset,
						rotationIds,
						responder,
					} = object!;

					const range = normalizeRange(
						{
							startEpoch,
							endEpoch,
							startOffset,
							endOffset,
						},
						outer,
					);

					const override = merge({}, range, {
						alias: single.alias!,
						rotations: rotationIds.map((value) => ({ id: value })),
						responder,
					});

					const context = {
						scheduleId: schedule.id,
					};

					openEditOverride({
						override,
						context,
						id: ['edit-override-button', schedule.id],
					});
				}}
			/>
		);
	}

	if (status === ShiftStatus.Multi) {
		return (
			<MultiShiftCard
				cards={cards}
				onClose={onClose}
				onQuickOverride={() => act(ShiftAction.AddOverride)}
			/>
		);
	}

	if (status === ShiftStatus.MultiQuickOverride) {
		return (
			<MultiQuickOverrideShiftCard
				cards={cards}
				onClose={onClose}
				onCancel={() => act(ShiftAction.Cancel)}
				isDisabled={!scheduleRights!.hasEditRight}
				onAddOverride={async (autoCompleteItem) => {
					const responder = {
						id: autoCompleteItem.atlassianIdentifier!,
						type: autoCompleteItem.type,
					};

					await addOverrideBulkAction({
						overrides: cards.map((period) => {
							const range = normalizeRange(period, outer);

							return {
								startTime: makeTime(range.startEpoch, range.startOffset),
								endTime: makeTime(range.endEpoch, range.endOffset),
								timezone: schedule.timezone,
								rotationIds: [period.rotation.id],
								responder: responder,
							};
						}),
					});

					onClose();
				}}
			/>
		);
	}

	return null;
};

export { ActiveCard };
